<template>
  <transition name="toast">
    <div 
      v-if="alert" 
      :class="[
        'fixed top-4 right-4 z-[9999] max-w-xs w-full rounded-xl shadow-xl overflow-hidden border border-white border-opacity-20',
        alertTypeClasses
      ]"
    >
      <div class="flex items-start p-4">
        <!-- Icon -->
        <div :class="iconClasses" class="flex-shrink-0 mt-0.5 mr-3">
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path v-if="alertType === 'success'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            <path v-if="alertType === 'error'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            <path v-if="alertType === 'info'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            <path v-if="alertType === 'warning'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86l-7 14A1 1 0 004 20h16a1 1 0 00.9-1.45l-7-14a1 1 0 00-1.8 0z" />
          </svg>
        </div>

        <!-- Content -->
        <div class="flex-1">
          <h3 :class="titleClasses" class="text-sm font-bold">
            {{ alertTitle }}
          </h3>
          <p :class="textClasses" class="mt-1 text-sm">
            {{ alert }}
          </p>
        </div>

        <!-- Close button -->
        <button 
          @click="$store.commit('clearAlert')" 
          class="ml-4 flex-shrink-0 text-white text-opacity-70 hover:text-opacity-100 focus:outline-none transition-opacity duration-200"
        >
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Progress bar -->
      <div class="h-1 w-full bg-black bg-opacity-20">
        <div 
          class="h-full transition-all duration-300 ease-linear" 
          :class="progressBarClasses"
          :style="{ width: progress + '%' }"
        ></div>
      </div>
    </div>
  </transition>
</template>

<script>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'AlertMessage',
  setup() {
    const store = useStore();
    const progress = ref(100);
    let interval = null;

    const alert = computed(() => store?.state?.alert || null);
    const alertType = computed(() => store?.state?.alertType || 'info');

    const alertTitleMap = {
      success: 'Success!',
      error: 'Error!',
      info: 'Info',
      warning: 'Warning!'
    };

    const alertTitle = computed(() => alertTitleMap[alertType.value] || 'Notification');

    // Use theme classes for info, fallback to color for others
    const alertTypeClasses = computed(() => {
      return {
        success: 'bg-gradient-to-r from-green-500 to-emerald-600',
        error: 'bg-gradient-to-r from-red-500 to-rose-600',
        info: 'bg-gradient-to-r from-primary-light to-primary-dark',
        warning: 'bg-gradient-to-r from-yellow-600 to-orange-600'
      }[alertType.value];
    });

    const iconClasses = 'text-white';

    const titleClasses = 'text-white';

    const textClasses = 'text-white text-opacity-90';

    const progressBarClasses = computed(() => {
      return {
        success: 'bg-emerald-300',
        error: 'bg-rose-300',
        info: 'bg-primary-light',
        warning: 'bg-orange-400'
      }[alertType.value];
    });

    // Progress bar logic
    const startProgressBar = () => {
      progress.value = 100;
      clearInterval(interval);

      interval = setInterval(() => {
        progress.value -= 1;
        if (progress.value <= 0) {
          clearInterval(interval);
          store.commit('clearAlert');
        }
      }, 50);
    };

    // Watch for alert changes to start progress bar
    watch(alert, (val) => {
      if (val) startProgressBar();
    });

    onBeforeUnmount(() => {
      clearInterval(interval);
    });

    return {
      alert,
      alertType,
      alertTitle,
      alertTypeClasses,
      iconClasses,
      titleClasses,
      textClasses,
      progressBarClasses,
      progress
    };
  }
};
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(100px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
.alert-message {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 10px 20px;
  border-radius: 4px;
  z-index: 1000;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
.info {
  background-color: #2196F3;
}
.success {
  background-color: #4CAF50;
}
.warning {
  background-color: #FF9800;
}
.error {
  background-color: #F44336;
}
</style>
